import { useState, useEffect } from 'react';
import { Popover, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'; // or your routing library
import { FronteggContext } from '@frontegg/rest-api';
// import ZoomIconImage from './path/to/your/zoom-icon'; // Update with your actual path
import ZoomIconImage from 'src/assets/zoom.svg';
import { UpgradeButton } from '../Payments/UpgradeButton';
import { AxiosAmbient } from 'src/lib';
import { analytics } from 'src/services';
import { useAuth } from '@frontegg/react';

const ZoomEncounterModal = ({ setShowZoomModal }) => {
  const [activePopover, setActivePopover] = useState(null);
  const [isZoomIntegrated, setIsZoomIntegrated] = useState(false);
  const [isZoomProAccount, setIsZoomProAccount] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get pro status
  const { currentPractitionerEntitlements } = useSelector(
    (state) => state?.practitionerState
  );
  const isAuraPro = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'pro'
  );

  useEffect(() => {
    const checkZoomIntegration = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
        };

        const result = await AxiosAmbient.get('/zoom-connection-status', {
          headers,
        });

        const response = result?.data;
        setIsZoomIntegrated(response?.connected === true);
      } catch (error) {
        console.error('Failed to check zoom integration:', error);
        setIsZoomIntegrated(false);
      }
    };

    const fetchZoomAccountType = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
        };

        const result = await AxiosAmbient.get('/zoom-account-type', {
          headers,
        });

        const response = result?.data;
        setIsZoomProAccount(response?.is_pro_or_higher);
        // Handle the zoom account type response as needed
      } catch (error) {
        console.error('Failed to fetch zoom account type:', error);
      }
    };

    checkZoomIntegration();
    fetchZoomAccountType();
  }, []);

  // Tooltip handlers
  const handleShowUpgradeTooltip = (element) => {
    if (!isAuraPro) {
      setActivePopover(element);
    }
  };

  const handleHideUpgradeTooltip = () => {
    setActivePopover(null);
  };

  // Click handler for zoom icon
  const handleZoomIconClick = () => {
    if (!isAuraPro) {
      // Case 2: User is not pro - show upgrade tooltip (handled by popover)
      analytics.track('Clicked Zoom Encounter Icon - Not Pro', {
        email: user?.email,
      });
      handleShowUpgradeTooltip('zoom');
      return;
    }

    if ((isAuraPro && !isZoomIntegrated) || !isZoomProAccount) {
      // Case 3: User is pro but not integrated - go to integrations
      analytics.track('Clicked Zoom Encounter Icon - Not Integrated', {
        email: user?.email,
      });
      navigate('/integrations');
      return;
    }

    if (isAuraPro && isZoomIntegrated) {
      // Case 1: User is pro and integrated - normal flow
      analytics.track('Clicked Zoom Encounter Icon - Success', {
        email: user?.email,
      });
      handleNormalZoomFlow();
    }
  };

  // Your normal zoom flow function
  const handleNormalZoomFlow = () => {
    setShowZoomModal(true);
  };

  // Render upgrade tooltip content
  const renderUpgradeTooltip = () => (
    <div
      onMouseEnter={() => handleShowUpgradeTooltip('zoom')}
      onMouseLeave={handleHideUpgradeTooltip}
    >
      <UpgradeButton
        text="Upgrade to access this feature"
        analyticsEvent="Clicked Zoom Encounter Button"
      />
    </div>
  );

  // Render the zoom icon based on conditions
  const renderZoomIcon = () => {
    if (!isAuraPro) {
      return (
        <img
          src={ZoomIconImage}
          height={30}
          width={30}
          className="mr-1.5 cursor-pointer opacity-50"
          onClick={handleZoomIconClick}
          onMouseEnter={() => {
            if (!isAuraPro) {
              handleShowUpgradeTooltip('zoom');
            }
          }}
          onMouseLeave={handleHideUpgradeTooltip}
          alt="Zoom"
        />
      );
    }

    if (isAuraPro && !isZoomIntegrated) {
      // Case 3: Pro but not integrated - Show connect tooltip
      return (
        <Tooltip title="Connect your Zoom account" placement="top">
          <img
            src={ZoomIconImage}
            height={30}
            width={30}
            className="mr-1.5 cursor-pointer opacity-50"
            onClick={handleZoomIconClick}
            alt="Zoom"
          />
        </Tooltip>
      );
    }

    if (isAuraPro && isZoomIntegrated) {
      // Case 1: Pro + Integrated - Normal flow with tooltip
      if (!isZoomProAccount) {
        return (
          <Tooltip
            title={
              <span>
                Only available with <strong>Zoom Pro</strong> subscription
              </span>
            }
            placement="top"
          >
            <img
              src={ZoomIconImage}
              height={30}
              width={30}
              className="mr-1.5 cursor-pointer opacity-50"
              onClick={handleZoomIconClick}
              alt="Zoom"
            />
          </Tooltip>
        );
      }
      return (
        <Tooltip title="Add to live meeting" placement="topRight">
          <img
            src={ZoomIconImage}
            height={30}
            width={30}
            className="mr-1.5 cursor-pointer"
            onClick={handleZoomIconClick}
            alt="Zoom"
          />
        </Tooltip>
      );
    }
  };

  return (
    <Popover
      content={renderUpgradeTooltip()}
      open={!isAuraPro && activePopover === 'zoom'}
      trigger="hover"
      placement="rightTop"
      mouseLeaveDelay={0.3}
      overlayInnerStyle={{ padding: '0px' }}
      align={{
        offset: [0, 0],
      }}
      onOpenChange={(visible) => {
        if (!visible) {
          handleHideUpgradeTooltip();
        }
      }}
    >
      {renderZoomIcon()}
    </Popover>
  );
};

export default ZoomEncounterModal;
